import { PermissionGate } from "@components/PermissionGate";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@utils/api";

import { clinicQKeys } from "@hooks/queries/clinic/queryKeys";

import { StyledResourceManagement } from "../../ResourceManagementTabs.styled";


interface Consumable {
  _id: string;
  name: string;
  price: number;
}

export const ConsumablesTab = () => {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [opened, setOpened] = useState(false);
  const [editing, setEditing] = useState<Consumable | null>(null);
  const form = useForm<{ name: string; price: number | string }>({
    initialValues: { name: "", price: 0 },
    validate: {
      name: (value) => (value.trim() ? null : t("consumable-name-required")),
      price: (value) =>
        typeof value === "number" && Number.isFinite(value) && value >= 0
          ? null
          : t("consumable-price-invalid"),
    },
  });
  // Share the raw response cache with the consultation consumable selector.
  const query = useQuery({
    queryKey: clinicQKeys.consumables(),
    queryFn: () => api.get<Consumable[]>("clinic/consumables"),
    select: (response) => response.data,
  });
  const mutation = useMutation({
    mutationFn: (values: { name: string; price: number }) =>
      editing
        ? api.patch(`clinic/consumables/${editing._id}`, values)
        : api.post("clinic/consumables", values),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: clinicQKeys.consumables() });
      setOpened(false);
      form.resetDirty();
    },
  });
  const openEditor = (item: Consumable | null) => {
    setEditing(item);
    const values = { name: item?.name || "", price: item?.price ?? 0 };
    form.setValues(values);
    form.resetDirty(values);
    form.clearErrors();
    mutation.reset();
    setOpened(true);
  };
  const close = () => {
    if (mutation.isLoading) return;
    if (form.isDirty() && !window.confirm(t("consumable-discard"))) return;
    setOpened(false);
  };
  const rows = (query.data || []).filter((item) =>
    item.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
  );
  const total = Math.max(1, Math.ceil(rows.length / 20));
  const currentPage = Math.min(page, total);

  return (
    <StyledResourceManagement>
      <Stack>
        <Group justify="space-between">
          <h1>{t("consumables")}</h1>
          <PermissionGate permission="resources.edit"><Button
            leftSection={<IconPlus size={16} />}
            onClick={() => openEditor(null)}
          >
            {t("add-consumable")}
          </Button></PermissionGate>
        </Group>
        <TextInput
          aria-label={t("search-consumables")}
          placeholder={t("search-consumables")}
          maw={400}
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            setPage(1);
          }}
        />
        {query.isLoading ? (
          <Loader />
        ) : query.isError ? (
          <Alert color="red">
            {t("encountered-error")}{" "}
            <Button variant="subtle" onClick={() => query.refetch()}>
              {t("retry")}
            </Button>
          </Alert>
        ) : (
          <>
            <Table.ScrollContainer minWidth={450}>
              <Table withTableBorder highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("name")}</Table.Th>
                    <Table.Th>{t("price")}</Table.Th>
                    <Table.Th>{t("action")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows
                    .slice((currentPage - 1) * 20, currentPage * 20)
                    .map((item) => (
                      <Table.Tr key={item._id}>
                        <Table.Td>{item.name}</Table.Td>
                        <Table.Td>
                          {new Intl.NumberFormat(i18n.language).format(
                            item.price
                          )}
                        </Table.Td>
                        <Table.Td>
                          <PermissionGate permission="resources.edit"><Button
                            variant="subtle"
                            size="xs"
                            onClick={() => openEditor(item)}
                          >
                            {t("edit")}
                          </Button></PermissionGate>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  {!rows.length && (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text c="dimmed">{t("no-consumables-found")}</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {total > 1 && (
              <Pagination
                value={currentPage}
                total={total}
                onChange={setPage}
              />
            )}
          </>
        )}
      </Stack>
      <Modal
        opened={opened}
        onClose={close}
        title={t(editing ? "edit-consumable" : "add-consumable")}
        centered
        closeOnClickOutside={!mutation.isLoading}
        closeOnEscape={!mutation.isLoading}
        withCloseButton={!mutation.isLoading}
      >
        <form
          onSubmit={form.onSubmit((values) =>
            mutation.mutate({
              name: values.name.trim(),
              price: Number(values.price),
            })
          )}
        >
          <Stack>
            <TextInput
              label={t("name")}
              required
              disabled={mutation.isLoading}
              {...form.getInputProps("name")}
            />
            <NumberInput
              label={t("price")}
              required
              min={0}
              disabled={mutation.isLoading}
              {...form.getInputProps("price")}
            />
            {mutation.isError && (
              <Alert color="red">{t("consumable-save-error")}</Alert>
            )}
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={close}
                disabled={mutation.isLoading}
              >
                {t("cancel")}
              </Button>
              <PermissionGate permission="resources.create"><Button
                type="submit"
                loading={mutation.isLoading}
                disabled={!!editing && !form.isDirty()}
              >
                {t("save")}
              </Button></PermissionGate>
            </Group>
          </Stack>
        </form>
      </Modal>
    </StyledResourceManagement>
  );
};
